// src/utils/proposalLayout.js
//
// Shared page geometry, color/type scales, and paging context for FIG
// proposal documents, per FIG_Proposal_Template_v1.md.
//
// The geometry here deliberately does NOT match invoicePdf.js or
// letterheadPdf.js. Those two share a 50/40 stationery grid; the proposal
// uses a 60/66 grid with a wider bottom margin to clear its footer band.
// Do not "align" these constants with the other two generators.
//
// This module does not modify — and must not be merged into — invoicePdf.js,
// letterheadPdf.js, or pdfText.js. Text measurement and drawing come from
// pdfText.js; nothing here reimplements them.

import { PDFDocument, StandardFonts } from 'pdf-lib';
import { toRgb, hexToRgb, drawTracked } from './pdfText';
// §1.6's footer string is locked copy; proposalLockedCopy is its single
// definition. Imported rather than duplicated so the two cannot drift.
import { FOOTER_DISCLAIMER } from './proposalLockedCopy';

// measureTracked and wrapTracked are intentionally not imported here — this
// module does no measuring or wrapping of its own. Callers building proposal
// sections should import them directly from './pdfText'.

// ---- §1.1 Page geometry ----

export const PAGE_WIDTH = 612;
export const PAGE_HEIGHT = 792;
export const MARGIN_LEFT = 60;
export const MARGIN_RIGHT = 60;
export const MARGIN_TOP = 60;
export const MARGIN_BOTTOM = 66;
export const CONTENT_WIDTH = 492; // PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT
export const BASELINE = 12;

/** Body leading (§1.3) and the minimum height of a table row. */
export const BODY_LEADING = 13;
export const MIN_ROW_HEIGHT = 18;

/** Baseline the footer band sits on (§1.6). */
export const FOOTER_BASELINE = 36;

/**
 * Floor that body content may not cross before breaking to a new page.
 *
 * Named floor rather than a raw margin comparison, following letterheadPdf's
 * approach. Unlike letterhead — where BOTTOM_MARGIN (40) sits *below* its
 * footer rule at 62, so it needed a separate FOOTER_RESERVE of 38 — the
 * proposal's MARGIN_BOTTOM (66) already clears FOOTER_BASELINE (36) by 30pt.
 * The margin is therefore the floor; no extra reserve is warranted, and
 * adding one would break pages early for no reason.
 */
export const CONTENT_FLOOR = MARGIN_BOTTOM;

// ---- §1.2 Color roles ----

/**
 * All seven §1.2 roles.
 *
 * brandAssets.getBrandAssets currently supplies only the first five —
 * FIG_Settings has no BrandGreyHex or BrandHairlineHex column yet — so this
 * map carries fallbacks for all seven rather than deferring to brandAssets
 * wholesale. Because each role resolves through toRgb against this map,
 * grey and hairline start being honored automatically the moment those two
 * columns are added upstream; no change is needed here.
 */
export const FALLBACK_COLORS = {
  primary: hexToRgb('#660099'),
  accent: hexToRgb('#B8860B'),
  // §1.2: "Reserved — recommended-option marker only." Not general-purpose.
  secondary: hexToRgb('#008b11'),
  // §1.2: "Reserved — not used in v1." Present for completeness only.
  tertiary: hexToRgb('#4A0033'),
  text: hexToRgb('#1A1A1A'),
  grey: hexToRgb('#6E6E6E'),
  hairline: hexToRgb('#DDDDDD'),
};

export const COLOR_ROLES = Object.keys(FALLBACK_COLORS);

/**
 * Resolve every §1.2 role against brandAssets, falling back per role.
 *
 * @param {Object} [brandAssets] shape from brandAssets.getBrandAssets
 * @returns {Object} role → pdf-lib rgb()
 */
export function resolveColors(brandAssets) {
  const out = {};
  for (const role of COLOR_ROLES) {
    out[role] = toRgb(brandAssets?.colors?.[role], FALLBACK_COLORS[role]);
  }
  return out;
}

// ---- §1.3 Type scale ----

/**
 * One entry per §1.3 role.
 *
 * `font` is the string 'regular' | 'bold' — callers resolve it against the
 * embedded fonts (ctx.fonts[TYPE.x.font]) rather than this module holding
 * font objects, so the scale stays a plain serializable description.
 *
 * `transform` is 'upper' | 'title' | 'asis'; applying it is the caller's job.
 *
 * Only Helvetica and Helvetica-Bold appear here. §1.3 forbids a second
 * typeface, so there is deliberately no Times entry — unlike invoicePdf.js
 * and letterheadPdf.js, which both embed TimesRomanBold for the 'F.I.G.'
 * text logo fallback.
 */
export const TYPE = {
  coverTitle:        { font: 'bold',    size: 26,  tracking: 1.6, transform: 'upper' },
  sectionCoverTitle: { font: 'bold',    size: 22,  tracking: 1.2, transform: 'upper' },
  sectionHeading:    { font: 'bold',    size: 14,  tracking: 0.6, transform: 'upper' },
  tableTitle:        { font: 'bold',    size: 9,   tracking: 0.8, transform: 'upper' },
  tableHeaderCell:   { font: 'bold',    size: 8.5, tracking: 0.3, transform: 'title' },
  tableBodyCell:     { font: 'regular', size: 9,   tracking: 0,   transform: 'asis' },
  bodyParagraph:     { font: 'regular', size: 9.5, tracking: 0,   transform: 'asis' },
  label:             { font: 'bold',    size: 9.5, tracking: 0,   transform: 'asis' },
  legal:             { font: 'regular', size: 8,   tracking: 0,   transform: 'asis' },
  footer:            { font: 'regular', size: 7.5, tracking: 0,   transform: 'asis' },
};

// ---- Context ----

/**
 * Create a proposal rendering context: document, fonts, resolved colors,
 * embedded brand images, and the paging primitives.
 *
 * @param {Object} [opts]
 * @param {Object} [opts.brandAssets] { logoBytes, leafBytes, colors }
 * @param {Object} [opts.settings] FIG_Settings record, carried for callers
 * @returns {Promise<Object>} proposal context
 */
export async function createProposalContext({ brandAssets, settings } = {}) {
  const pdfDoc = await PDFDocument.create();

  // Exactly two fonts. §1.3 forbids a second typeface, so the text fallback
  // for a failed logo embed uses HelveticaBold, not TimesRomanBold.
  const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const fonts = { regular, bold };

  const colors = resolveColors(brandAssets);

  // Same try-png-then-jpg pattern as invoicePdf.js.
  let logoImage = null;
  if (brandAssets?.logoBytes) {
    try {
      logoImage = await pdfDoc.embedPng(brandAssets.logoBytes);
    } catch {
      try { logoImage = await pdfDoc.embedJpg(brandAssets.logoBytes); } catch { /* text fallback */ }
    }
  }

  let leafImage = null;
  if (brandAssets?.leafBytes) {
    try {
      leafImage = await pdfDoc.embedPng(brandAssets.leafBytes);
    } catch {
      try { leafImage = await pdfDoc.embedJpg(brandAssets.leafBytes); } catch { /* skip */ }
    }
  }

  const pages = [];
  const coverPages = new Set();
  let cursor = PAGE_HEIGHT - MARGIN_TOP;

  // Horizontal overrun log. ensureSpace only guards the BOTTOM of the page;
  // nothing below drawTracked can see the right margin, so every text draw that
  // goes through ctx.overflow() reports here when it passes the column edge.
  // Diagnostic only — nothing in the render path reads it.
  const overflows = [];
  let currentPart = null;

  /**
   * The drawTracked `overflow` option for this context: the column's right edge,
   * the shared sink, and whichever §2 part is currently drawing. Built fresh per
   * call so `part` is read at draw time.
   */
  function overflow() {
    return { rightEdge: MARGIN_LEFT + CONTENT_WIDTH, sink: overflows, part: currentPart };
  }

  /**
   * Watermark leaf (§1.5). Lifted verbatim from letterheadPdf.js
   * drawWatermark, which already matches the spec exactly.
   *
   * NOTE: the comments in invoicePdf.js ("~60% visible") and letterheadPdf.js
   * are wrong about the crop. With wmW 600 and wmX = PAGE_WIDTH - 400 = 212,
   * the visible span is 612 - 212 = 400pt of a 600pt image — 66.7% visible,
   * 33.3% bleeding off the right edge. The geometry is correct; only the
   * annotation is off.
   */
  function drawWatermark(page) {
    if (!leafImage) return;
    const dims = leafImage.scale(1);
    const wmW = 600;
    const wmH = (dims.height / dims.width) * wmW;
    const wmX = PAGE_WIDTH - 400;          // bleeds off the right edge
    const wmY = (PAGE_HEIGHT - wmH) / 2;
    page.drawImage(leafImage, { x: wmX, y: wmY, width: wmW, height: wmH, opacity: 0.06 });
  }

  /**
   * Add a page, watermark it, and reset the cursor to the top margin.
   *
   * No header furniture is drawn: §1.6 specifies no running header on content
   * pages, unlike letterheadPdf's drawCompactHeader.
   *
   * @param {Object} [opts]
   * @param {boolean} [opts.isDocumentCover=false]
   *   Exempts the page from the §1.6 footer. Set this ONLY for part 1 of §2 —
   *   the single document cover. §1.6 exempts "every page except the cover",
   *   singular, and that is the only page it means.
   *
   *   Section covers (§2 parts 4 and 5, "Section cover + content") are NOT
   *   document covers. They are the opening pages of content runs: they DO
   *   receive the footer and they DO count toward M. Create them with no flag
   *   at all.
   * @returns {Object} the new page
   */
  function newPage({ isDocumentCover = false } = {}) {
    const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    pages.push(page);
    if (isDocumentCover) coverPages.add(page);
    drawWatermark(page);
    cursor = PAGE_HEIGHT - MARGIN_TOP;
    return page;
  }

  /**
   * Break to a new page if `needed` points do not remain above CONTENT_FLOOR.
   *
   * @param {number} needed
   * @returns {boolean} true if a page break happened
   */
  function ensureSpace(needed) {
    if (cursor - needed < CONTENT_FLOOR) {
      newPage();
      return true;
    }
    return false;
  }

  /**
   * Draw the footer band on every non-cover page, then save.
   *
   * Runs as a post-render pass so body content cannot overwrite it. Page
   * numbering counts ALL pages including covers, so a skipped cover still
   * consumes a number and 'Page 2 of 3' can be the first footer shown.
   *
   * @returns {Promise<Uint8Array>}
   */
  function finalize() {
    const total = pages.length;
    const spec = TYPE.footer;
    const font = fonts[spec.font];

    for (let i = 0; i < total; i++) {
      const page = pages[i];
      if (coverPages.has(page)) continue;

      drawTracked(page, FOOTER_DISCLAIMER, {
        x: MARGIN_LEFT,
        y: FOOTER_BASELINE,
        font,
        size: spec.size,
        color: colors.grey,
        tracking: spec.tracking,
      });

      drawTracked(page, `Page ${i + 1} of ${total}`, {
        x: PAGE_WIDTH - MARGIN_RIGHT,
        y: FOOTER_BASELINE,
        font,
        size: spec.size,
        color: colors.grey,
        tracking: spec.tracking,
        align: 'right',
      });
    }

    return pdfDoc.save();
  }

  return {
    pdfDoc,
    fonts,
    colors,
    logoImage,
    leafImage,
    pages,
    settings,

    get cursor() { return cursor; },
    set cursor(v) { cursor = v; },

    /** Name of the §2 part currently drawing; tags overflow entries. */
    get currentPart() { return currentPart; },
    set currentPart(v) { currentPart = v; },

    /** Every text draw that passed the column's right edge. */
    overflows,
    overflow,

    /** True if `page` is the document cover (footer-exempt per §1.6). */
    isDocumentCover(page) { return coverPages.has(page); },

    drawWatermark,
    newPage,
    ensureSpace,
    finalize,
  };
}
